import { useEffect, useState } from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroupProps } from "@mui/material";
import { TimeValidationError } from "@mui/x-date-pickers";
import { useSelector } from "react-redux";
import { utcToZonedTime } from "date-fns-tz";
import { isDefined } from "@clipboard-health/util-ts";
import { DEFAULT_TIMEZONE } from "src/constants/timezone";
import { SessionType } from "src/modules/interface";
import { MultiInputTimeRangeField } from "@mui/x-date-pickers-pro/MultiInputTimeRangeField";

import { DefaultShiftTimes } from "@src/containers/facilityDashboard/ShiftCalendar/api";
import { convertShiftTimeToDateTime } from "@src/containers/facilityDashboard/ShiftCalendar/newShift/shiftDate";
import { ShiftName } from "@src/appV2/ShiftBlock/types/shift";
import { isAfter } from "date-fns";

export interface ShiftTypeTimeSelectionProps {
  shiftType: ShiftName;
  onChange?: (value: SelectedShiftInfo) => void;
  shiftTime: DefaultShiftTimes[keyof DefaultShiftTimes];
  selectedDate?: Date;
}

export interface SelectedShiftInfo {
  shiftType: ShiftName;
  start?: Date;
  end?: Date;
  isSelected: boolean;
}

const shiftTypeColor: Record<string, ToggleButtonGroupProps["color"]> = {
  am: "warning",
  pm: "info",
  noc: "primary",
};

export function ShiftTypeTimeSelection(props: ShiftTypeTimeSelectionProps) {
  const { shiftType, onChange, shiftTime, selectedDate } = props;
  const { profile } = useSelector((state: SessionType) => state.session);
  const { startTime, endTime } = convertShiftTimeToDateTime({ shiftTime, startDate: selectedDate });
  const selectedDateISO = selectedDate?.toISOString();
  const facilityTimeZone = profile?.tmz ?? DEFAULT_TIMEZONE;

  const [shiftStartTime, setShiftStartTime] = useState<Date>();
  const [shiftEndTime, setShiftEndTime] = useState<Date>();
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [timeError, setTimeError] = useState<TimeValidationError | "invalidRange">();

  function onShiftInfoChange(params: Partial<SelectedShiftInfo>) {
    onChange?.({
      shiftType,
      start: shiftStartTime,
      end: shiftEndTime,
      isSelected,
      ...params,
    });
  }

  useEffect(() => {
    setShiftStartTime(undefined);
    setShiftEndTime(undefined);
    setTimeError(undefined);
    setIsSelected(false);
  }, [selectedDateISO]);

  return (
    <Stack spacing={1} direction="row">
      <Box sx={{ width: "56px" }}>
        <ToggleButton
          value="check"
          color={shiftTypeColor[shiftType]}
          selected={isSelected}
          onChange={() => {
            setTimeError(undefined);
            setIsSelected(!isSelected);
            if (isAfter(startTime, utcToZonedTime(new Date(), facilityTimeZone))) {
              onShiftInfoChange({ isSelected: !isSelected, end: endTime, start: startTime });
            }
            setShiftStartTime(isSelected ? undefined : startTime);
            setShiftEndTime(isSelected ? undefined : endTime);
          }}
          sx={{ width: "100%", height: "100%" }}
          disabled={!isDefined(selectedDate)}
        >
          {shiftType.toUpperCase()}
        </ToggleButton>
      </Box>
      <MultiInputTimeRangeField
        value={[shiftStartTime, shiftEndTime]}
        slotProps={{
          textField: ({ position }) => ({
            label: position === "start" ? "Start time" : "End time",
            helperText:
              timeError === "minTime"
                ? "Time can't be in the past."
                : timeError === "invalidRange"
                ? "End time should be greater"
                : "",
          }),
        }}
        onChange={([start, end]) => {
          setShiftStartTime(start ?? undefined);
          setShiftEndTime(end ?? undefined);
          onShiftInfoChange({ start: start ?? undefined, end: end ?? undefined });
        }}
        disableIgnoringDatePartForTimeValidation={true}
        minTime={utcToZonedTime(new Date(), facilityTimeZone)}
        onError={([start, end]) => {
          setTimeError(start ?? end);
        }}
        disabled={!isSelected}
      />
    </Stack>
  );
}
